import type { DocumentSummary, FaceObservations, ValidationReport } from '@photovalidator/core';

/**
 * Application state.
 *
 * One store, one direction of flow:
 *   bitmap → (worker) analysis + faces → validator-core → report → overlay/checklist
 *
 * The UI never computes measurements: it renders `report.measurements`.
 * There is no viewport, no zoom and no second (edited) bitmap: the photo shown
 * on the stage is always the file the user selected, bit for bit.
 */

export type AppPhase = 'empty' | 'decoding' | 'analysing' | 'quick' | 'deep' | 'advanced' | 'error';

export type SourceKind = 'file' | 'camera';

export interface SourceImage {
  /** The decoded upload. The validator never re-encodes or crops it. */
  bitmap: ImageBitmap;
  width: number;
  height: number;
  sourceKind: SourceKind;
  fileName?: string;
  sizeBytes?: number;
  /** Sniffed from magic bytes — never from the client-provided type. */
  detectedMimeType?: string;
  declaredMimeType?: string;
}

/**
 * Metadata about the pipeline run that is useful to the interface.
 *
 * The pixel buffer intentionally stays inside the worker: the main thread never
 * needs it, and not keeping a copy is one more guarantee that the photo is not
 * handed around the UI.
 */
export interface Preprocessed {
  faces: FaceObservations;
  decodeMs: number;
  analyseMs: number;
  faceModel: 'ready' | 'unavailable';
  faceModelReason?: string;
}

export interface AppState {
  documentId: string;
  documents: DocumentSummary[];
  phase: AppPhase;
  error?: string;
  image?: SourceImage;
  preprocessed?: Preprocessed;
  /** Fast report: technical + geometry only. */
  quickReport?: ValidationReport;
  /** Full report including quality/background — the single source of truth. */
  report?: ValidationReport;
  showOverlay: boolean;
  detailMode: boolean;
  /** Id of the measurement currently highlighted from the checklist. */
  highlightId?: string;
  busyLabel?: string;
  /** Technical error text, shown only in the debug panel. */
  debugError?: string;
  /**
   * Non-fatal notices about missing runtime capabilities (face model not
   * loaded, quality stage skipped…). They are aggregated into ONE friendly
   * banner: the raw reasons are only listed in `?debug=1`.
   */
  capabilityIssues: string[];
  analyticsEnabled: boolean;
}

export const INITIAL_STATE: AppState = {
  documentId: '',
  documents: [],
  phase: 'empty',
  showOverlay: true,
  detailMode: false,
  capabilityIssues: [],
  analyticsEnabled: true,
};
